import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { AfterViewInit, Component, DestroyRef, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from '@angular/router';
import * as mapboxgl from 'mapbox-gl';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { AuthService } from '../../../../../@core/auth/auth.service';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { SessionService } from '../../../../../@core/utils/session.service';
import { Customer } from '../../../../modules/contracts/models/customer';
import { CustomerService } from '../../../../modules/contracts/services/customer.service';
import { FarmDTO } from '../../../models/farm';
import { FarmsService } from '../../../services/farms.service';

function isValidEmailAddress(value: string): boolean {
  const trimmed = (value || '').trim();
  if (!trimmed) { return false; }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

function emailWithTldValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value || '').trim();
  if (!value) { return null; }
  if (Validators.email(control)) { return { email: true }; }
  return isValidEmailAddress(value) ? null : { email: true };
}

@Component({
  selector: 'farm-detail',
  templateUrl: './farm-detail.component.html',
  styleUrls: ['./farm-detail.component.scss']
})
export class FarmDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  public editFarm: FarmDTO;
  public loading: boolean = true;
  public submitting: boolean = false;
  private farmId: number;
  public showFields: boolean = false;
  public units: string[] = ["gal", "L", "qt", "mL", "fl oz"];
  public customersList: Customer[];
  myControl = new FormControl('');
  filteredCustomers$: Observable<any[]>; // Holds the filtered list for autocomplete

  public emailList: string[] = [];
  public emailControl = new FormControl('', [emailWithTldValidator]);
  public emailAddAttempted = false;

  public notificationEmailList: string[] = [];
  public notificationEmailControl = new FormControl('', [emailWithTldValidator]);
  public notificationEmailAddAttempted = false;
  public readonly notificationEmailSeparatorKeyCodes: number[] = [ENTER, COMMA, SPACE];

  @ViewChild('notificationEmailSection') notificationEmailSectionRef: ElementRef<HTMLElement>;
  @ViewChild('notificationEmailInputRef') notificationEmailInputRef: ElementRef<HTMLInputElement>;
  @ViewChild('locationMap') locationMapRef: ElementRef<HTMLElement>;

  // Optional GPS location picker
  private map: mapboxgl.Map;
  private locationMarker: mapboxgl.Marker;
  public locationSearching = false;
  public locationSearchFailed = false;


  constructor(
    private route: ActivatedRoute,
    private session: SessionService,
    private farmService: FarmsService,
    private authService: AuthService,
    private errorService: ErrorService,
    private router: Router,
    public dialogRef: MatDialogRef<any>,
    private customersService: CustomerService,
  ) {
    this.notificationEmailControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.notificationEmailAddAttempted = false;
    });

    this.emailControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.emailAddAttempted = false;
    });
  }

  ngOnInit() {
    if(this.authService.hasRole('spray-recs:admin') == true || this.authService.hasRole('application-recs:admin') == true){
    this.showFields = true;
  } else {
    this.showFields = false;
  }

    this.farmId = +this.route.snapshot.paramMap.get('id') || 0;

    this.loading = true;
    this.customersService.getCustomerList().subscribe((x) => {
      this.customersList = (x || []).slice().sort((a, b) => {
        const nameA = a && a.name ? a.name.toLowerCase() : '';
        const nameB = b && b.name ? b.name.toLowerCase() : '';
        return nameA.localeCompare(nameB);
      });
      this.loading = false;
     
      this.filteredCustomers$ = this.myControl.valueChanges.pipe(
        startWith(''),
        map(value => this.filterCustomers(value || ''))
      );

      if (this.editFarm.customerId) {
        const customer:any = this.findCustomer(this.editFarm.customerId);
        if (customer) {
          this.myControl.setValue(customer);  // Set the customer object for editing
        }
      }
      
    })

    // if the session has a selected farm and the id passed in matches, use that
    this.editFarm = this.session.selectedFarm;
    this.parseNotificationEmails();
    this.parseEmails();
    // else if we have an ID, but the session farm is empty (someone clicked refresh in the browser)
    if (this.farmId !== undefined && this.session.selectedFarm === undefined && this.farmId !== 0) {
      this.editFarm = {
        isActive: true
      };

      this.farmService.apiFarmsByIdGet(this.farmId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:f => {
        this.editFarm = f;
        this.parseNotificationEmails();
        this.parseEmails();
        //this.loading = false;
      }, error:err => { this.errorService.handleError(err); }});
    } else {
      if (this.editFarm === undefined) {
        this.editFarm = {};
      }
      if (this.editFarm.id === null || this.editFarm.id === undefined || this.editFarm.id === 0) {
        this.editFarm.isActive = true;
      }
      this.parseNotificationEmails();
      this.parseEmails();
      //this.loading = false;
    }

  }
  ngAfterViewInit() {
    // The dialog is still animating open, so let it settle before sizing the map.
    setTimeout(() => this.initLocationMap());
  }

  ngOnDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private initLocationMap(): void {
    if (!this.locationMapRef || this.map) { return; }
    (mapboxgl as any).accessToken = environment.MAPBOX_KEY;

    const hasLocation = this.hasLocation();
    this.map = new mapboxgl.Map({
      container: this.locationMapRef.nativeElement,
      style: 'mapbox://styles/mapbox/satellite-streets-v11',
      center: hasLocation ? [this.editFarm.longitude, this.editFarm.latitude] : [-98, 38.88],
      zoom: hasLocation ? 13 : 3.5
    });
    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    this.map.on('click', (e) => this.setLocation(e.lngLat.lat, e.lngLat.lng));
    this.map.on('load', () => {
      this.map.resize();
      this.drawBlockPolygons();
    });

    if (hasLocation) {
      this.placeMarker(this.editFarm.latitude, this.editFarm.longitude);
    } else {
      this.fitToFarmBlocks();
    }
  }

  // Read-only outlines of the farm's blocks, built from each block's active GPS points (one ring per polygonId).
  private drawBlockPolygons(): void {
    const features: any[] = [];
    ((this.editFarm && this.editFarm.blocks) || []).forEach(b => {
      const byPolygon: { [key: string]: any[] } = {};
      (b.gpsPoints || []).filter(p => p.isActive && p.latitude != null && p.longitude != null).forEach(p => {
        (byPolygon[p.polygonId] = byPolygon[p.polygonId] || []).push(p);
      });
      Object.keys(byPolygon).forEach(key => {
        const pts = byPolygon[key];
        if (pts.length < 3) { return; }
        pts.sort((a, c) => (a.sortOrder != null && c.sortOrder != null) ? a.sortOrder - c.sortOrder : a.id - c.id);
        const ring = pts.map(p => [+p.longitude, +p.latitude]);
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) { ring.push([first[0], first[1]]); }
        features.push({ type: 'Feature', properties: { name: b.name }, geometry: { type: 'Polygon', coordinates: [ring] } });
      });
    });
    if (!features.length || this.map.getSource('farm-blocks')) { return; }

    this.map.addSource('farm-blocks', { type: 'geojson', data: { type: 'FeatureCollection', features } as any });
    this.map.addLayer({ id: 'farm-blocks-fill', type: 'fill', source: 'farm-blocks', paint: { 'fill-color': '#ff8c00', 'fill-opacity': 0.35 } });
    this.map.addLayer({ id: 'farm-blocks-line', type: 'line', source: 'farm-blocks', paint: { 'line-color': '#ff6600', 'line-width': 2 } });
    this.map.addLayer({
      id: 'farm-blocks-label', type: 'symbol', source: 'farm-blocks',
      layout: { 'text-field': ['get', 'name'], 'text-size': 12 },
      paint: { 'text-color': '#ffffff', 'text-halo-color': '#000000', 'text-halo-width': 1.5 }
    });
  }

  // With no saved farm location, frame the farm's existing blocks (GPS points, else block lat/long).
  private fitToFarmBlocks(): void {
    const blocks = (this.editFarm && this.editFarm.blocks) || [];
    const bounds = new mapboxgl.LngLatBounds();
    const extend = (lng: any, lat: any) => {
      if (lng != null && lat != null && +lng !== 0 && +lat !== 0) {
        bounds.extend([+lng, +lat] as [number, number]);
      }
    };

    blocks.forEach(b => (b.gpsPoints || []).filter(p => p.isActive).forEach(p => extend(p.longitude, p.latitude)));
    if (bounds.isEmpty()) {
      blocks.forEach(b => extend(b.longitude, b.latitude));
    }
    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, { padding: 60, maxZoom: 16, duration: 0 });
    }
  }

  public hasLocation(): boolean {
    return this.editFarm && typeof this.editFarm.latitude === 'number' && typeof this.editFarm.longitude === 'number';
  }

  private placeMarker(lat: number, lng: number): void {
    if (!this.map) { return; }
    if (!this.locationMarker) {
      this.locationMarker = new mapboxgl.Marker({ draggable: true, color: '#d32f2f' })
        .setLngLat([lng, lat])
        .addTo(this.map);
      this.locationMarker.on('dragend', () => {
        const pos = this.locationMarker.getLngLat();
        this.setLocation(pos.lat, pos.lng);
      });
    } else {
      this.locationMarker.setLngLat([lng, lat]);
    }
  }

  private roundCoordinate(value: number): number {
    return Math.round(value * 1e6) / 1e6;
  }

  private setLocation(lat: number, lng: number, fly: boolean = false): void {
    this.editFarm.latitude = this.roundCoordinate(lat);
    this.editFarm.longitude = this.roundCoordinate(lng);
    this.placeMarker(this.editFarm.latitude, this.editFarm.longitude);
    if (fly && this.map) {
      this.map.flyTo({ center: [this.editFarm.longitude, this.editFarm.latitude], zoom: Math.max(this.map.getZoom(), 13) });
    }
  }

  // Called when the user types a latitude/longitude by hand.
  public onCoordinateInput(): void {
    const lat = this.editFarm.latitude;
    const lng = this.editFarm.longitude;
    const validLat = typeof lat === 'number' && lat >= -90 && lat <= 90;
    const validLng = typeof lng === 'number' && lng >= -180 && lng <= 180;
    if (validLat && validLng) {
      this.setLocation(lat, lng, true);
    }
  }

  public clearLocation(): void {
    this.editFarm.latitude = null;
    this.editFarm.longitude = null;
    if (this.locationMarker) {
      this.locationMarker.remove();
      this.locationMarker = null;
    }
  }

  public async locateFromAddress(): Promise<void> {
    const query = [this.editFarm.streetAddress, this.editFarm.city, this.editFarm.state, this.editFarm.zipCode]
      .filter(x => x && x.toString().trim())
      .join(', ');
    if (!query) {
      this.locationSearchFailed = true;
      return;
    }

    this.locationSearching = true;
    this.locationSearchFailed = false;
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?limit=1&access_token=${environment.MAPBOX_KEY}`;
      const response = await fetch(url);
      const result = await response.json();
      const center = result && result.features && result.features[0] && result.features[0].center;
      if (center) {
        this.setLocation(center[1], center[0], true);
      } else {
        this.locationSearchFailed = true;
      }
    } catch {
      this.locationSearchFailed = true;
    } finally {
      this.locationSearching = false;
    }
  }

  private filterCustomers(value: string): any[] {
    if (!this.customersList) return []; // Prevent errors if data isn't loaded
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
  
    if (!filterValue) {
      return this.customersList;
    }

    return this.customersList.filter(customer => 
      customer.name && customer.name.toLowerCase().includes(filterValue)
    );
    // const filterValue = value.toLowerCase();
    // return this.customersList.filter(option => option.name.toLowerCase().includes(filterValue));
  }
  displayFn(value: any): string {
    return value && value.name ? value.name : '';
  }

  private parseEmails(): void {
    const raw = this.editFarm && this.editFarm.email;
    if (!raw) {
      this.emailList = [];
      return;
    }
    this.emailList = raw.split(/[,;\s]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0);
  }

  private syncEmails(): void {
    this.editFarm.email = this.emailList.join(',');
  }

  public addEmail(event: MatChipInputEvent): void {
    const input = event.input;
    const rawValue = event.value || '';
    const tokens = rawValue.split(/[,;\s]+/).map(t => t.trim()).filter(t => t.length > 0);

    if (!tokens.length) {
      if (input) { input.value = ''; }
      return;
    }

    let addedAny = false;
    let hasInvalid = false;

    tokens.forEach(token => {
      if (isValidEmailAddress(token)) {
        if (this.emailList.indexOf(token) === -1) {
          this.emailList.push(token);
          addedAny = true;
        }
      } else {
        hasInvalid = true;
      }
    });

    this.emailAddAttempted = hasInvalid;
    if (addedAny) {
      this.syncEmails();
    }

    if (input) { input.value = ''; }
    this.emailControl.reset();
  }

  public removeEmail(email: string): void {
    const index = this.emailList.indexOf(email);
    if (index >= 0) {
      this.emailList.splice(index, 1);
      this.syncEmails();
    }
  }

  public removeLastEmailOnBackspace(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (input && !input.value && this.emailList.length > 0) {
      this.emailList.pop();
      this.syncEmails();
    }
  }

  private parseNotificationEmails(): void {
    const raw = this.editFarm && this.editFarm.eventNotificationEmail;
    if (!raw) {
      this.notificationEmailList = [];
      return;
    }
    this.notificationEmailList = raw.split(/[,;\s]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0);
  }

  private syncNotificationEmails(): void {
    this.editFarm.eventNotificationEmail = this.notificationEmailList.join(',');
  }

  public addNotificationEmail(event: MatChipInputEvent): void {
    const input = event.input;
    const rawValue = event.value || '';
    const tokens = rawValue.split(/[,;\s]+/).map(t => t.trim()).filter(t => t.length > 0);

    if (!tokens.length) {
      if (input) { input.value = ''; }
      return;
    }

    let addedAny = false;
    let hasInvalid = false;

    tokens.forEach(token => {
      if (isValidEmailAddress(token)) {
        if (this.notificationEmailList.indexOf(token) === -1) {
          this.notificationEmailList.push(token);
          addedAny = true;
        }
      } else {
        hasInvalid = true;
      }
    });

    this.notificationEmailAddAttempted = hasInvalid;
    if (hasInvalid) {
      this.scrollNotificationEmailSectionIntoView(false);
    }
    if (addedAny) {
      this.syncNotificationEmails();
    }

    if (input) { input.value = ''; }
    this.notificationEmailControl.reset();
  }

  public removeNotificationEmail(email: string): void {
    const index = this.notificationEmailList.indexOf(email);
    if (index >= 0) {
      this.notificationEmailList.splice(index, 1);
      this.syncNotificationEmails();
    }
  }

  public removeLastNotificationEmailOnBackspace(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (input && !input.value && this.notificationEmailList.length > 0) {
      this.notificationEmailList.pop();
      this.syncNotificationEmails();
    }
  }

  public onNotificationsEnabledChange(enabled: boolean): void {
    if (enabled) {
      this.scrollNotificationEmailSectionIntoView(true);
    }
  }

  private isFullyVisibleWithin(el: HTMLElement, container: HTMLElement): boolean {
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    return elRect.top >= containerRect.top && elRect.bottom <= containerRect.bottom;
  }

  private scrollNotificationEmailSectionIntoView(focusInput: boolean): void {
    setTimeout(() => {
      const sectionEl = this.notificationEmailSectionRef && this.notificationEmailSectionRef.nativeElement;
      if (sectionEl) {
        const scrollContainer = sectionEl.closest('.mat-mdc-dialog-content') as HTMLElement;
        const alreadyVisible = scrollContainer && this.isFullyVisibleWithin(sectionEl, scrollContainer);
        if (!alreadyVisible) {
          sectionEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      if (focusInput) {
        const inputEl = this.notificationEmailInputRef && this.notificationEmailInputRef.nativeElement;
        if (inputEl) { inputEl.focus(); }
      }
    });
  }

  setFarmInactive() {
    this.editFarm.isActive = false;
    this.onSubmit();
  }

  public deleteClicked() {
  }

 public onCustomerSelected(customer : Customer){
  if(customer !=null){
    this.editFarm.customerId = customer.id;
  }
 }

 public findCustomer(id: number){
  if(this.customersList){
    return this.customersList.find(x => x.id == id);
  }

 }
  

  public onCustomerDropdownChange(id: number) {
    if (id || 0 !== 0) {
      this.editFarm.customerId = id;
    }
  }

  public onSubmit() {
    if (this.editFarm.name) {
      this.editFarm.name = this.editFarm.name.trim();
    }

    // Location is all-or-nothing: a half-filled pair is dropped rather than saved.
    if (!this.hasLocation()) {
      this.editFarm.latitude = null;
      this.editFarm.longitude = null;
    }

    if (this.editFarm.id === null || this.editFarm.id === undefined || this.editFarm.id === 0) {
      // Close right away and let FarmsTableComponent create the farm in the
      // background, so the dialog doesn't block on the API call.
      this.dialogRef.close({
        backgroundSave: true,
        isNew: true,
        farm: { ...this.editFarm }
      });
      return;
    }

    // Close right away and let FarmsTableComponent perform the update
    // in the background, so the dialog doesn't block on the API call.
    this.dialogRef.close({
      backgroundSave: true,
      isNew: false,
      farm: { ...this.editFarm }
    });
  }

  closeDialog(cancelled: boolean = false) {
    this.dialogRef.close(cancelled);
  }
  public identify(index, item) {
    return item.name;
  }
}
